
import React, { useState, useEffect } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import TemplatePicker from "../../components/TemplatePicker";

const YAHOO_APP_PASSWORD_HELP = "https://help.yahoo.com/kb/mail/generate-password-sln15241.html";
const YAHOO_ACCOUNT_SECURITY  = "https://login.yahoo.com/account/security?.lang=en-US&.intl=us&.src=yhelp";
const ZOHO_APP_PASSWORD_HELP  = "https://help.zoho.com/portal/en/kb/accounts/manage-your-zoho-account/articles/mfa-application-specific-passwords";
const ZOHO_SMTP_HELP          = "https://www.zoho.com/mail/help/zoho-smtp.html";

export default function EmailCenter({ user }) {
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const accent  = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";

  // Panels: employees see "company" + "templates" + "blast"
  //         admins also get "personal" (their own SMTP) and "setup" (company SMTP setup)
  const [panel, setPanel] = useState("company");
  const [showYahooHelp, setShowYahooHelp] = useState(false);

  // Status
  const [status, setStatus]             = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);

  // ── Personal connect form (admins only) ──
  const [pEmail, setPEmail] = useState("");
  const [pLoading, setPLoading] = useState(false);

  // ── Company email setup form (admins only) ──
  const [cEmail, setCEmail]     = useState("");
  const [cPass, setCPass]       = useState("");
  const isEbekmed = user?.company === "ebekmed";
  const [cHost, setCHost]       = useState(isEbekmed ? "smtppro.zoho.com" : "smtp.bizmail.yahoo.com");
  const [cPort, setCPort]       = useState("465");
  const [cName, setCName]       = useState("");
  const [cLoading, setCLoading] = useState(false);

  // ── Templates ──
  const [templates, setTemplates] = useState([]);
  const [tmplEdit, setTmplEdit]   = useState(null);
  const [tmplName, setTmplName]   = useState("");
  const [tmplSubject, setTmplSubj] = useState("");
  const [tmplBody, setTmplBody]   = useState("");

  // ── Blast ──
  const [leads, setLeads]       = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bSubject, setBSubject] = useState("");
  const [bBody, setBBody]         = useState("");
  const [bTmpl, setBTmpl]         = useState("");
  const [blasting, setBlasting]   = useState(false);
  const [showEmailPicker, setShowEmailPicker] = useState(false);
  const [results, setResults]   = useState(null);
  const [filterQ, setFilterQ]   = useState("");
  const [stageFilter, setStageFilter] = useState("all");

  // ── Load status ──
  const loadStatus = () => {
    API.get("/email/status").then(r => setStatus(r.data)).catch(() => {});
    API.get("/email/company-config").then(r => setCompanyConfig(r.data)).catch(() => {});
  };

  useEffect(() => { loadStatus(); }, []);

  useEffect(() => {
    if (panel === "templates" || panel === "blast") {
      API.get("/email/templates").then(r => setTemplates(Array.isArray(r.data) ? r.data : (r.data.templates || []))).catch(() => setTemplates([]));
    }
    if (panel === "blast") {
      API.get("/leads/team").then(r => setLeads(r.data.leads || r.data || [])).catch(() => {});
    }
  }, [panel]);

  // ── Personal connect / disconnect ──
  const connectPersonal = async () => {
    if (!pEmail.trim()) return toast.error("Official email is required");
    if (!/@(ebekindia|ebekmed)\.com$/i.test(pEmail.trim())) return toast.error("Use your official Ebek or Ebekmed email");
    setPLoading(true);
    try {
      await API.post("/email/connect-official", { email: pEmail.trim() });
      toast.success("Personal email connected!");
      loadStatus();
      setPanel("company");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || "Connection failed");
    }
    setPLoading(false);
  };

  const disconnectPersonal = async () => {
    await API.post("/email/disconnect");
    loadStatus();
    toast.success("Personal email disconnected");
  };

  // ── Company email save / remove ──
  const saveCompanyEmail = async () => {
    if (!cEmail.trim() || !cPass.trim()) return toast.error("Email and App Password required");
    setCLoading(true);
    try {
      await API.post("/email/company-config", {
        email:        cEmail.trim(),
        app_password: cPass.trim(),
        smtp_host:    cHost.trim(),
        smtp_port:    cPort,
        display_name: cName.trim() || null,
      });
      toast.success("Company email saved — all employees can now send emails");
      setCPass("");
      loadStatus();
      setPanel("company");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.error || "Connection failed");
    }
    setCLoading(false);
  };

  const removeCompanyEmail = async () => {
    await API.delete("/email/company-config");
    loadStatus();
    toast.success("Company email removed");
  };

  const testEmail = async () => {
    try {
      const r = await API.post("/email/test");
      toast.success(r.data.message || "Test email sent!");
    } catch (e) { toast.error(e.response?.data?.error || "Test failed"); }
  };

  // ── Templates ──
  const saveTmpl = async () => {
    if (!tmplName.trim() || !tmplBody.trim()) return toast.error("Name and body required");
    try {
      if (tmplEdit) {
        await API.put(`/email/templates/${tmplEdit.id}`, { name: tmplName, subject: tmplSubject, body: tmplBody });
        toast.success("Template updated");
      } else {
        await API.post("/email/templates", { name: tmplName, subject: tmplSubject, body: tmplBody });
        toast.success("Template created");
      }
      setTmplEdit(null); setTmplName(""); setTmplSubj(""); setTmplBody("");
      API.get("/email/templates").then(r => setTemplates(r.data.templates || []));
    } catch { toast.error("Save failed"); }
  };

  // ── Blast ──
  const runBlast = async () => {
    if (!bSubject.trim() || !bBody.trim()) return toast.error("Subject and body required");
    if (!selected.size) return toast.error("Select at least one lead");
    setBlasting(true); setResults(null);
    try {
      const r = await API.post("/email/bulk", {
        lead_ids:    Array.from(selected),
        subject:     bSubject.trim(),
        body:        bBody.trim(),
        template_id: bTmpl || null,
        delay_ms:    5000,
      });
      setResults(r.data);
      toast.success(`Sent: ${r.data.summary.sent} | Failed: ${r.data.summary.failed}`);
    } catch { toast.error("Blast failed"); }
    setBlasting(false);
  };

  const filteredLeads = leads.filter(l => {
    const stage = String(l.pipeline_stage || l.lead_stage || "").toLowerCase();
    const matchesStage = stageFilter === "all" || stage === stageFilter;
    const q = filterQ.toLowerCase();
    const matchesText = !q || l.institution_name?.toLowerCase().includes(q) || l.contact_name?.toLowerCase().includes(q);
    return matchesStage && matchesText;
  });

  // ── Styles ──
  const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: 20, marginBottom: 16 };
  const btn  = (active, extra = {}) => ({
    padding: "9px 20px", border: "none", borderRadius: 20,
    background: active ? accent : "#f1f5f9",
    color: active ? "#fff" : "#64748b",
    fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
    ...extra,
  });
  const inp  = { padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, fontFamily: "inherit", outline: "none", width: "100%", boxSizing: "border-box" };
  const tag  = (color, bg) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 800, color, background: bg });

  // Overall email readiness
  const emailReady = status?.personal || companyConfig?.configured;
  const activeEmail = status?.personal
    ? status.personal_email
    : (companyConfig?.configured ? companyConfig.smtp_email : null);
  const emailLabel = status?.personal ? "Your personal email" : (companyConfig?.configured ? "Company shared email" : null);

  return (
    <>
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      {/* ── Header ── */}
      <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 28 }}>✉️</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>Email Centre</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              {emailReady
                ? <><strong style={{ color: "#16a34a" }}>✅ Ready</strong> — {emailLabel}: {activeEmail}</>
                : <span style={{ color: "#f59e0b" }}>⚠️ No email configured{isAdmin ? " — set up Company Email below" : " — ask your admin"}</span>
              }
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={btn(showYahooHelp, { border: "1px solid #bfdbfe" })} onClick={() => setShowYahooHelp(true)}>📖 Yahoo Setup</button>
          <button style={btn(panel === "company")} onClick={() => setPanel("company")}>📬 Email Status</button>
          {isAdmin && <button style={btn(panel === "setup")} onClick={() => setPanel("setup")}>⚙️ Company Email</button>}
          {isAdmin && <button style={btn(panel === "personal")} onClick={() => setPanel("personal")}>👤 My Email</button>}
          <button style={btn(panel === "templates")} onClick={() => setPanel("templates")}>📝 Templates</button>
          <button style={btn(panel === "blast")} onClick={() => setPanel("blast")}>📤 Blast</button>
        </div>
      </div>

      {showYahooHelp && (
        <div role="dialog" aria-modal="true" aria-label="Yahoo Bizmail setup instructions"
          style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.58)", display: "grid", placeItems: "center", padding: 16 }}
          onClick={() => setShowYahooHelp(false)}>
          <div style={{ ...card, width: "min(560px, 100%)", maxHeight: "85vh", overflowY: "auto", margin: 0 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 20, fontWeight: 900, color: "#1e293b" }}>📖 Connect Your Official Mailbox</div>
                <div style={{ marginTop: 4, fontSize: 13, color: "#64748b" }}>Yahoo Bizmail for @ebekindia.com · Zoho Mail for @ebekmed.com</div>
              </div>
              <button type="button" onClick={() => setShowYahooHelp(false)} aria-label="Close Yahoo setup instructions"
                style={{ border: 0, borderRadius: 10, width: 36, height: 36, background: "#f1f5f9", color: "#334155", fontSize: 20, cursor: "pointer" }}>×</button>
            </div>

            <div style={{ marginTop: 16, padding: 14, borderRadius: 12, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", fontSize: 13, lineHeight: 1.55 }}>
              <strong>Important:</strong> Do not enter your normal Yahoo/Bizmail login password in CRM. Generate a separate App Password for Ebek CRM.
            </div>

            <ol style={{ margin: "16px 0", paddingLeft: 22, color: "#334155", fontSize: 14, lineHeight: 1.8 }}>
              <li>Sign in to <a href={YAHOO_ACCOUNT_SECURITY} target="_blank" rel="noopener noreferrer" style={{ color: accent, fontWeight: 700 }}>Yahoo Account Security</a> using your official mailbox.</li>
              <li>Open <strong>External connections</strong> and select <strong>Create app password</strong>.</li>
              <li>Choose <strong>Other</strong> and name it <strong>Ebek CRM</strong>.</li>
              <li>Generate and copy the password shown by Yahoo.</li>
              <li>In CRM, open <strong>My Profile → Email Settings</strong>.</li>
              <li>Confirm your official email, paste the App Password, save, and send a test email.</li>
            </ol>

            <div style={{ padding: 12, borderRadius: 10, background: "#eff6ff", color: "#1e40af", fontSize: 13 }}>
              SMTP server: <strong>smtp.bizmail.yahoo.com</strong> · Port <strong>465</strong> with SSL. CRM automatically tries port 587 if required.
            </div>

            <a href={YAHOO_ACCOUNT_SECURITY} target="_blank" rel="noopener noreferrer"
              style={{ display: "block", marginTop: 16, padding: "12px 16px", borderRadius: 12, textAlign: "center", textDecoration: "none", background: accent, color: "#fff", fontWeight: 800 }}>
              Sign in to Yahoo Account Security ↗
            </a>
            <a href={YAHOO_APP_PASSWORD_HELP} target="_blank" rel="noopener noreferrer"
              style={{ display: "block", marginTop: 8, padding: "12px 16px", borderRadius: 12, textAlign: "center", textDecoration: "none", background: "#f1f5f9", color: "#334155", fontWeight: 700 }}>
              Open Official Yahoo Instructions ↗
            </a>
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
              <div style={{ fontWeight: 900, color: "#0f766e" }}>@ebekmed.com — Zoho Mail</div>
              <ol style={{ margin: "8px 0", paddingLeft: 22, color: "#334155", fontSize: 14, lineHeight: 1.8 }}>
                <li>Sign in to Zoho Accounts and open <strong>Security</strong>.</li>
                <li>Open <strong>Application Specific Passwords</strong> and select <strong>Generate New Password</strong>.</li>
                <li>Name it <strong>Ebek CRM</strong>, generate it, and copy the code shown once.</li>
                <li>In CRM Profile → Email Settings, choose <strong>Zoho Mail</strong>, enter the complete @ebekmed.com address and the generated code.</li>
              </ol>
              <div style={{ padding: 12, borderRadius: 10, background: "#ecfeff", color: "#0f766e", fontSize: 13 }}>
                Organization SMTP: <strong>smtppro.zoho.com</strong> · Port <strong>465 SSL</strong> or <strong>587 TLS</strong>.
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <a href={ZOHO_APP_PASSWORD_HELP} target="_blank" rel="noopener noreferrer" style={{ color: accent, fontWeight: 800 }}>Zoho app-password guide ↗</a>
                <a href={ZOHO_SMTP_HELP} target="_blank" rel="noopener noreferrer" style={{ color: accent, fontWeight: 800 }}>Zoho SMTP guide ↗</a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── EMAIL STATUS PANEL (all roles) ── */}
      {panel === "company" && (
        <div style={card}>
          <div style={{ fontSize: 16, fontWeight: 900, color: "#1e293b", marginBottom: 16 }}>Email Configuration</div>

          {/* Company email status */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, marginBottom: 12, background: companyConfig?.configured ? "#f0fdf4" : "#f8fafc" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b" }}>Company Email</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>Shared outbound account for all employees</div>
              </div>
              {companyConfig?.configured
                ? <span style={tag("#15803d", "#dcfce7")}>✅ Configured</span>
                : <span style={tag("#92400e", "#fef3c7")}>⚠️ Not set</span>
              }
            </div>
            {companyConfig?.configured && (
              <div style={{ marginTop: 12, fontSize: 13, color: "#374151", display: "grid", gap: 4 }}>
                <div><strong>From:</strong> {companyConfig.smtp_email}</div>
                {companyConfig.display_name && <div><strong>Display name:</strong> {companyConfig.display_name}</div>}
                <div><strong>SMTP:</strong> {companyConfig.smtp_host}:{companyConfig.smtp_port}</div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                  All employees send outbound emails through this account.
                </div>
              </div>
            )}
            {!companyConfig?.configured && isAdmin && (
              <div style={{ marginTop: 10 }}>
                <button onClick={() => setPanel("setup")}
                  style={{ padding: "9px 18px", background: accent, color: "#fff", border: "none", borderRadius: 10, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>
                  Set Up Company Email →
                </button>
              </div>
            )}
          </div>

          {/* Personal email status (for current user) */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, background: status?.personal ? "#f0fdf4" : "#f8fafc" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b" }}>Your Personal Email</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                  Overrides company email when set. Sends appear from your address.
                </div>
              </div>
              {status?.personal
                ? <span style={tag("#15803d", "#dcfce7")}>✅ Connected</span>
                : <span style={tag("#64748b", "#f1f5f9")}>— Not connected</span>
              }
            </div>
            {status?.personal && (
              <div style={{ marginTop: 10, fontSize: 13, color: "#374151" }}>
                <strong>From:</strong> {status.personal_email}
              </div>
            )}
            {status?.personal && (
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={testEmail}
                  style={{ padding: "8px 16px", background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>
                  Send Test
                </button>
                <button onClick={disconnectPersonal}
                  style={{ padding: "8px 16px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>
                  Disconnect
                </button>
              </div>
            )}
            {!status?.personal && (
              <div style={{ marginTop: 10 }}>
                <button onClick={() => setPanel("personal")}
                  style={{ padding: "8px 16px", background: "#f1f5f9", color: "#374151", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 12 }}>
                  Connect personal email →
                </button>
              </div>
            )}
          </div>

          {/* Info box */}
          <div style={{ marginTop: 12, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 14, fontSize: 12, color: "#1e40af" }}>
            <strong>How it works:</strong> Emails sent from lead cards use your personal email if connected, otherwise the company shared email.
            Leads see your name as the sender in both cases.
          </div>
        </div>
      )}

      {/* ── COMPANY EMAIL SETUP (admin only) ── */}
      {panel === "setup" && isAdmin && (
        <div style={card}>
          {companyConfig?.configured ? (
            <>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#15803d" }}>Company Email Active</div>
                <div style={{ color: "#64748b", marginTop: 4 }}>{companyConfig.smtp_email}</div>
                {companyConfig.display_name && <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>Display name: {companyConfig.display_name}</div>}
                <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 4 }}>{companyConfig.smtp_host}:{companyConfig.smtp_port}</div>
                <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>
                  All employees can now send emails to leads through this account.
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button onClick={testEmail}
                  style={{ padding: "10px 20px", background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Send Test Email
                </button>
                <button onClick={() => { setCEmail(companyConfig.smtp_email); setCHost(companyConfig.smtp_host); setCPort(String(companyConfig.smtp_port)); setCName(companyConfig.display_name || ""); setCompanyConfig(c => ({ ...c, configured: false })); }}
                  style={{ padding: "10px 20px", background: "#eff6ff", color: "#1d4ed8", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Update Credentials
                </button>
                <button onClick={removeCompanyEmail}
                  style={{ padding: "10px 20px", background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  Remove
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 style={{ margin: "0 0 6px", color: "#1e293b" }}>Set Up Company Email</h3>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
                One shared {isEbekmed ? "Zoho Mail" : "Yahoo Business"} account — all employees will use this to send emails to leads.
              </p>

              {/* Yahoo App Password guide */}
              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 14, marginBottom: 20 }}>
                <strong style={{ color: "#1e40af", fontSize: 13 }}>📋 How to get a {isEbekmed ? "Zoho Mail" : "Yahoo Business"} App Password:</strong>
                <ol style={{ margin: "10px 0 0", paddingLeft: 20, color: "#1e40af", fontSize: 13, lineHeight: 2 }}>
                  <li>Sign in to <strong>{isEbekmed ? "accounts.zoho.com" : "Yahoo Account Security"}</strong> using the sending account</li>
                  <li>Open <strong>Security</strong> → <strong>{isEbekmed ? "Application Specific Passwords" : "External connections / App Passwords"}</strong></li>
                  <li>Select <strong>Generate New Password</strong></li>
                  <li>Name it <code>Ebek CRM</code> and select <strong>Generate</strong></li>
                  <li>Copy the app password shown — it appears only once</li>
                  <li>Paste it below instead of the normal mailbox login password</li>
                </ol>
                <div style={{ marginTop: 10, fontSize: 12, color: "#1e40af", background: "#dbeafe", borderRadius: 8, padding: "8px 12px" }}>
                  <strong>Note:</strong> If the option is unavailable, ask the mail administrator to enable app passwords or MFA for the account.
                </div>
              </div>

              <div style={{ display: "grid", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#64748b", marginBottom: 5, textTransform: "uppercase" }}>Company Email Address</label>
                  <input
                    placeholder="e.g. sales@ebekindia.com"
                    value={cEmail} onChange={e => setCEmail(e.target.value)}
                    style={inp}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#64748b", marginBottom: 5, textTransform: "uppercase" }}>App Password (from {isEbekmed ? "Zoho Security" : "Yahoo Security"})</label>
                  <input
                    placeholder="16-character app password"
                    type="password" value={cPass} onChange={e => setCPass(e.target.value)}
                    style={inp}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#64748b", marginBottom: 5, textTransform: "uppercase" }}>Display Name (optional)</label>
                  <input
                    placeholder="e.g. Ebek Education Sales"
                    value={cName} onChange={e => setCName(e.target.value)}
                    style={inp}
                  />
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Shown as sender name in recipients' inbox</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 8 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#64748b", marginBottom: 5, textTransform: "uppercase" }}>SMTP Host</label>
                    <input value={cHost} onChange={e => setCHost(e.target.value)} style={inp} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 800, color: "#64748b", marginBottom: 5, textTransform: "uppercase" }}>Port</label>
                    <input value={cPort} onChange={e => setCPort(e.target.value)} style={inp} />
                  </div>
                </div>
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, fontSize: 12, color: "#64748b" }}>
                  <strong>{isEbekmed ? "Zoho organization default:" : "Yahoo Business default:"}</strong> {isEbekmed ? "smtppro.zoho.com" : "smtp.bizmail.yahoo.com"}:465 (SSL); CRM automatically tries port 587 when required.
                </div>
                <button onClick={saveCompanyEmail} disabled={cLoading}
                  style={{ padding: "13px", background: cLoading ? "#94a3b8" : accent, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                  {cLoading ? "⏳ Verifying & Saving..." : "✅ Save Company Email"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── PERSONAL EMAIL (admin optional) ── */}
      {panel === "personal" && (
        <div style={card}>
          <h3 style={{ margin: "0 0 6px", color: "#1e293b" }}>Your Official Email</h3>
          <p style={{ margin: "0 0 16px", fontSize: 13, color: "#64748b" }}>
            Optional. Overrides the company email for emails you send — recipients see your personal address.
          </p>

          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: 14, marginBottom: 20 }}>
            <strong style={{ color: "#1e40af", fontSize: 13 }}>Yahoo Business / Turbify — Setup:</strong>
            <ol style={{ margin: "8px 0 0", paddingLeft: 18, color: "#1e40af", fontSize: 13, lineHeight: 2 }}>
              <li>Log in to <strong>mail.turbify.com</strong> or <strong>business.yahoo.com</strong></li>
              <li>Go to <strong>Account Security → App Passwords</strong></li>
              <li>Generate an app password for <code>Ebek CRM</code></li>
              <li>Paste that 16-character password below</li>
            </ol>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            <input type="email" placeholder="your.name@ebekindia.com" value={pEmail} onChange={e => setPEmail(e.target.value)} style={inp} />
            <button onClick={connectPersonal} disabled={pLoading}
              style={{ padding: "12px", background: pLoading ? "#94a3b8" : accent, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
              {pLoading ? "⏳ Verifying..." : "Connect My Email"}
            </button>
          </div>
        </div>
      )}

      {/* ── TEMPLATES ── */}
      {panel === "templates" && (
        <div>
          <div style={card}>
            <h3 style={{ margin: "0 0 16px", color: "#1e293b" }}>{tmplEdit ? "Edit Template" : "New Email Template"}</h3>
            <div style={{ display: "grid", gap: 12 }}>
              <input placeholder="Template name" value={tmplName} onChange={e => setTmplName(e.target.value)} style={inp} />
              <input placeholder="Subject line (optional)" value={tmplSubject} onChange={e => setTmplSubj(e.target.value)} style={inp} />
              <textarea
                placeholder={"Email body. Use {{name}} for contact name.\nE.g. Dear {{name}},\n\nFollowing up on our visit..."}
                value={tmplBody} onChange={e => setTmplBody(e.target.value)}
                rows={7} style={{ ...inp, resize: "vertical" }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={saveTmpl}
                  style={{ padding: "10px 24px", background: accent, color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {tmplEdit ? "Update" : "Save"}
                </button>
                {tmplEdit && (
                  <button onClick={() => { setTmplEdit(null); setTmplName(""); setTmplSubj(""); setTmplBody(""); }}
                    style={{ padding: "10px 16px", background: "#f1f5f9", color: "#64748b", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>

          <div style={card}>
            <h3 style={{ margin: "0 0 14px", color: "#1e293b" }}>Saved Templates ({templates.length})</h3>
            {templates.length === 0 && <div style={{ color: "#94a3b8", fontSize: 13 }}>No templates yet.</div>}
            {templates.map(t => (
              <div key={t.id} style={{ padding: "12px 14px", border: "1px solid #e2e8f0", borderRadius: 12, marginBottom: 10, background: "#f8fafc" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{t.name}</div>
                    {t.subject && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Subject: {t.subject}</div>}
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6, whiteSpace: "pre-wrap" }}>{t.body?.slice(0, 120)}{t.body?.length > 120 ? "…" : ""}</div>
                  </div>
                  <button
                    onClick={() => { setTmplEdit(t); setTmplName(t.name); setTmplSubj(t.subject || ""); setTmplBody(t.body || ""); window.scrollTo(0, 0); }}
                    style={{ padding: "6px 14px", background: "#eff6ff", color: "#1d4ed8", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 12, whiteSpace: "nowrap" }}>
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── BLAST ── */}
      {panel === "blast" && (
        <div>
          {!emailReady && (
            <div style={{ ...card, background: "#fef3c7", border: "1px solid #fcd34d" }}>
              <strong style={{ color: "#92400e" }}>⚠️ No email configured</strong>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: "#92400e" }}>
                {isAdmin
                  ? "Set up the company email in ⚙️ Company Email before sending."
                  : "Ask your admin to configure the company email account."}
              </p>
            </div>
          )}

          <div style={card}>
            <h3 style={{ margin: "0 0 6px", color: "#1e293b" }}>Email Blast</h3>
            <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>
              Sends via <strong>{activeEmail || "company email"}</strong> with 5-second spacing between sends.
              Use <code style={{ background: "#f1f5f9", padding: "1px 4px", borderRadius: 4 }}>{"{{name}}"}</code> for the contact name.
            </div>

            <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input placeholder="Subject line" value={bSubject} onChange={e => setBSubject(e.target.value)}
                  style={{ ...inp, flex: 1 }} />
                <button onClick={() => setShowEmailPicker(true)}
                  style={{ padding: "10px 14px", background: "#e0f2fe", border: "none", borderRadius: 10,
                    fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                    color: "#0369a1", whiteSpace: "nowrap" }}>
                  📝 Template
                </button>
              </div>
              <textarea placeholder="Email body..." value={bBody} onChange={e => setBBody(e.target.value)}
                rows={6} style={{ ...inp, resize: "vertical" }} />
            </div>

            {/* Lead selector */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>
                  Select recipients ({selected.size} selected)
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setSelected(new Set(filteredLeads.filter(l => l.contact_email).map(l => l.id)))}
                    style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                    Select all with email
                  </button>
                  <button onClick={() => setSelected(new Set())}
                    style={{ padding: "5px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                    Clear
                  </button>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"minmax(160px, .7fr) minmax(220px, 1.3fr)", gap:8, marginBottom:8 }}>
                <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} style={inp} aria-label="Filter recipients by stage">
                  <option value="all">All stages</option>
                  <option value="visited">Visited</option><option value="interested">Interested</option>
                  <option value="proposal">Proposal</option><option value="mou_shared">MOU Shared</option>
                  <option value="mou_signed">MOU Signed</option><option value="payment_pending">Payment Pending</option>
                  <option value="payment_made">Payment Made</option>
                </select>
                <input placeholder="Search and manually select recipients..." value={filterQ} onChange={e => setFilterQ(e.target.value)} style={inp} />
              </div>
              <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 10 }}>
                {filteredLeads.map(l => (
                  <label key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 14px", borderBottom: "1px solid #f1f5f9", cursor: "pointer", background: selected.has(l.id) ? "#eff6ff" : "#fff" }}>
                    <input type="checkbox" checked={selected.has(l.id)} onChange={e => {
                      setSelected(s => { const n = new Set(s); e.target.checked ? n.add(l.id) : n.delete(l.id); return n; });
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.institution_name}</div>
                      <div style={{ fontSize: 11, color: l.contact_email ? "#64748b" : "#f59e0b" }}>
                        {l.contact_name} {l.contact_email ? `— ${l.contact_email}` : "— no email"}
                      </div>
                    </div>
                  </label>
                ))}
                {filteredLeads.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>No leads found</div>}
              </div>
            </div>

            <button onClick={runBlast} disabled={blasting || !emailReady}
              style={{ width: "100%", padding: "13px", background: blasting || !emailReady ? "#94a3b8" : accent, color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800, cursor: blasting || !emailReady ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {blasting ? "⏳ Sending (5s between each)..." : `📤 Send to ${selected.size} lead${selected.size !== 1 ? "s" : ""}`}
            </button>

            {results && (
              <div style={{ marginTop: 14, background: "#f8fafc", borderRadius: 12, padding: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b", marginBottom: 8 }}>
                  Results — Sent: <span style={{ color: "#16a34a" }}>{results.summary.sent}</span> | Failed: <span style={{ color: "#dc2626" }}>{results.summary.failed}</span> | Skipped: <span style={{ color: "#f59e0b" }}>{results.summary.skipped}</span>
                </div>
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {results.results.map((r, i) => (
                    <div key={i} style={{ fontSize: 12, padding: "4px 0", color: r.status === "sent" ? "#16a34a" : r.status === "failed" ? "#dc2626" : "#f59e0b", borderBottom: "1px solid #f1f5f9" }}>
                      {r.status === "sent" ? "✅" : r.status === "failed" ? "❌" : "⏭️"} {r.institution_name || `Lead #${r.lead_id}`}
                      {r.reason ? ` — ${r.reason}` : ""}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

    {/* Email Template Picker */}
    {showEmailPicker && (
      <TemplatePicker
        type="email"
        accent={accent}
        vars={{ sender_name: user?.name || "", company_name: user?.company === "ebekmed" ? "EbekMed" : "Ebek" }}
        onSelect={({ subject, body }) => {
          if (subject) setBSubject(subject);
          setBBody(body);
          toast.success("Template loaded");
        }}
        onClose={() => setShowEmailPicker(false)}
      />
    )}
    </>
  );
}
